import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import { Button, Form } from "react-bootstrap";
import { addLottery, getLotteryList } from "../../../api/lottery/lotteryApi";
import CustomInput from "../lib/CustomInput";
import Select from "react-select";
import { getMatches } from "../../../api/match/matchApi";
import { toast } from "react-toastify";

interface MatchOption {
  value: number;
  label: string;
}

interface LotteryFormValues {
  menMin: number;
  womenMin: number;
  count: number;
  match: number | null;
}

interface LotteryResult {
  men: Array<{
    firstName: string;
    lastName: string;
    nationalCode: string;
    mobile: string;
  }>;
  women: Array<{
    firstName: string;
    lastName: string;
    nationalCode: string;
    mobile: string;
  }>;
}

function LotteryPage() {
  const [results, setResults] = useState<LotteryResult | null>(null);
  const [matches, setMatches] = useState<any[]>([]);
  const [matchOptions, setMatchOptions] = useState<MatchOption[]>([]);
  const [lotteryList, setLotteryList] = useState<any[]>([]);
  const [reservedList, setReservedList] = useState<any[]>([]);
  const [wonList, setWonList] = useState<any[]>([]);
  const [reservedData, setReservedData] = useState<LotteryResult | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // دریافت لیست بازی‌ها
        const matchesResponse = await getMatches();
        const options = matchesResponse.map((match: any) => ({
          value: match.id,
          label: `${match.homeTeam?.nameFA || "تیم میزبان"} - ${
            match.awayTeam?.nameFA || "تیم مهمان"
          }`,
        }));
        setMatchOptions(options);
        setMatches(matchesResponse);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  const formik = useFormik<LotteryFormValues>({
    initialValues: {
      menMin: 0,
      womenMin: 0,
      count: 0,
      match: null,
    },
    onSubmit: async (values) => {
      if (!values.match) {
        alert("لطفا بازی را انتخاب کنید");
        return;
      }
      try {
        const numericValues = {
          menMin: Number(values.menMin),
          womenMin: Number(values.womenMin),
          count: Number(values.count),
          match: values.match,
        };
        const response = await addLottery(numericValues);

        // دریافت لیست قرعه کشی‌ها بعد از ایجاد قرعه کشی
        const lotteryResponse = await getLotteryList({
          page: 1,
          numberInPage: 10,
          match: values.match,
        });

        // جداسازی لیست‌ها بر اساس type
        const reservedItems = lotteryResponse.data.filter(
          (item: any) => item.type === "reserved"
        );
        const wonItems = lotteryResponse.data.filter(
          (item: any) => item.type === "won"
        );

        // Transform the data based on the actual response structure
        // لیست برندگان با فیلتر نوع "won"
        const transformedData: LotteryResult = {
          men: response
            .filter((item: any) => item.male && item.type === "won")
            .map((item: any) => ({
              firstName: item.ticket?.user?.f_name || "",
              lastName: item.ticket?.user?.l_name || "",
              nationalCode: item.nationalCode || "",
              mobile: item.phone || "",
            })),
          women: response
            .filter((item: any) => !item.male && item.type === "won")
            .map((item: any) => ({
              firstName: item.ticket?.user?.f_name || "",
              lastName: item.ticket?.user?.l_name || "",
              nationalCode: item.nationalCode || "",
              mobile: item.phone || "",
            })),
        };

        // تبدیل داده‌های رزرو با استفاده از همان داده‌های برندگان ولی با فیلتر نوع "reserved"
        const reservedData: LotteryResult = {
          men: response
            .filter((item: any) => item.male && item.type === "reserved")
            .map((item: any) => ({
              firstName: item.ticket?.user?.f_name || "",
              lastName: item.ticket?.user?.l_name || "",
              nationalCode: item.nationalCode || "",
              mobile: item.phone || "",
            })),
          women: response
            .filter((item: any) => !item.male && item.type === "reserved")
            .map((item: any) => ({
              firstName: item.ticket?.user?.f_name || "",
              lastName: item.ticket?.user?.l_name || "",
              nationalCode: item.nationalCode || "",
              mobile: item.phone || "",
            })),
        };

        setReservedList(reservedItems);
        setWonList(wonItems);
        setReservedData(reservedData);
        setResults(transformedData);
        toast.success("قرعه کشی با موفقیت انجام شد");
      } catch (error) {
        console.error("Error in lottery:", error);
        toast.error("خطا در انجام قرعه کشی");
      }
    },
  });

  return (
    <>
      <div className="card mb-5">
        <div className="card-header">
          <h3 className="card-title">قرعه کشی</h3>
        </div>
        <Form onSubmit={formik.handleSubmit}>
          <div className="card-body">
            <div className="row mb-6">
              <div className="col-6">
                <label className="form-label fw-bold">انتخاب بازی</label>
                <Select
                  className="basic-select"
                  classNamePrefix="select"
                  value={
                    matchOptions.find(
                      (option) => option.value === formik.values.match
                    ) || null
                  }
                  onChange={(selectedOption) => {
                    formik.setFieldValue(
                      "match",
                      selectedOption ? selectedOption.value : null
                    );
                  }}
                  onBlur={() => formik.setFieldTouched("match", true)}
                  options={matchOptions}
                  isLoading={!matches}
                  placeholder="انتخاب بازی"
                />
                {formik.touched.match && !formik.values.match && (
                  <div className="text-danger">انتخاب بازی الزامی است</div>
                )}
              </div>
            </div>

            <div className="row mb-6">
              <div className="col-6">
                <CustomInput
                  formik={formik}
                  label="حداقل تعداد آقایان"
                  placeholder="حداقل تعداد آقایان"
                  fieldName="menMin"
                  type="number"
                  required
                />
              </div>
              <div className="col-6">
                <CustomInput
                  formik={formik}
                  label="حداقل تعداد خانم‌ها"
                  placeholder="حداقل تعداد خانم‌ها"
                  fieldName="womenMin"
                  type="number"
                  required
                />
              </div>
            </div>

            <div className="row mb-6">
              <div className="col-6">
                <CustomInput
                  formik={formik}
                  label="تعداد کل"
                  placeholder="تعداد کل"
                  fieldName="count"
                  type="number"
                  required
                />
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                <Button type="submit" variant="primary">
                  انجام قرعه کشی
                </Button>
              </div>
            </div>
          </div>
        </Form>

        {results && (
          <div className="card-body">
            <h3 className="card-title">لیست برنده‌ها</h3>
            <div className="row">
              <div className="col-6">
                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title">آقایان</h3>
                  </div>
                  <div className="card-body">
                    <div className="table-responsive">
                      <table className="table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3">
                        <thead>
                          <tr className="fw-bold text-muted">
                            <th>ردیف</th>
                            <th>نام</th>
                            <th>نام خانوادگی</th>
                            <th>کد ملی</th>
                            <th>شماره موبایل</th>
                          </tr>
                        </thead>
                        <tbody>
                          {results.men.map((person, index) => (
                            <tr key={index}>
                              <td>{index + 1}</td>
                              <td>{person.firstName}</td>
                              <td>{person.lastName}</td>
                              <td>{person.nationalCode}</td>
                              <td>{person.mobile}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-6">
                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title">خانم‌ها</h3>
                  </div>
                  <div className="card-body">
                    <div className="table-responsive">
                      <table className="table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3">
                        <thead>
                          <tr className="fw-bold text-muted">
                            <th>ردیف</th>
                            <th>نام</th>
                            <th>نام خانوادگی</th>
                            <th>کد ملی</th>
                            <th>شماره موبایل</th>
                          </tr>
                        </thead>
                        <tbody>
                          {results.women.map((person, index) => (
                            <tr key={index}>
                              <td>{index + 1}</td>
                              <td>{person.firstName}</td>
                              <td>{person.lastName}</td>
                              <td>{person.nationalCode}</td>
                              <td>{person.mobile}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {reservedData &&
          (reservedData.men.length > 0 || reservedData.women.length > 0) && (
            <div className="card-body">
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">لیست رزروها</h3>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-6">
                      <div className="card">
                        <div className="card-header">
                          <h3 className="card-title">آقایان</h3>
                        </div>
                        <div className="card-body">
                          <div className="table-responsive">
                            <table className="table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3">
                              <thead>
                                <tr className="fw-bold text-muted">
                                  <th>ردیف</th>
                                  <th>نام</th>
                                  <th>نام خانوادگی</th>
                                  <th>کد ملی</th>
                                  <th>شماره موبایل</th>
                                </tr>
                              </thead>
                              <tbody>
                                {reservedData.men.map((person, index) => (
                                  <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>{person.firstName}</td>
                                    <td>{person.lastName}</td>
                                    <td>{person.nationalCode}</td>
                                    <td>{person.mobile}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-6">
                      <div className="card">
                        <div className="card-header">
                          <h3 className="card-title">بانوان</h3>
                        </div>
                        <div className="card-body">
                          <div className="table-responsive">
                            <table className="table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3">
                              <thead>
                                <tr className="fw-bold text-muted">
                                  <th>ردیف</th>
                                  <th>نام</th>
                                  <th>نام خانوادگی</th>
                                  <th>کد ملی</th>
                                  <th>شماره موبایل</th>
                                </tr>
                              </thead>
                              <tbody>
                                {reservedData.women.map((person, index) => (
                                  <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>{person.firstName}</td>
                                    <td>{person.lastName}</td>
                                    <td>{person.nationalCode}</td>
                                    <td>{person.mobile}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
      </div>
    </>
  );
}

export default LotteryPage;
